import React, { useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * Public ramp-calculator long-form SEO content.
 *
 * Now reduced to ONLY the FAQ section per user direction — all
 * other reference sections (gradient guide, measuring guide, ramp-type
 * picker, materials, Australia-wide supply, Melbourne install) were
 * removed to keep the page minimal, premium and conversion-focused.
 *
 * The 8 FAQ questions exactly match the FAQPage JSON-LD in
 * /app/frontend/public/index.html and the /faq.xml feed — single
 * source of truth for Google rich results.
 */
const FAQS = [
  ["How do I measure rise for a ramp?",
   "Stand a spirit level across the top of your threshold or step and drop a tape measure vertically down to the lower surface. Take three readings (left, centre, right) — on uneven ground always design to the largest figure. Rise is the single most important measurement for sizing a ramp."],
  ["What ramp gradient should I choose?",
   "For everyday accessibility, 1:14 is the gentle, widely accepted choice. 1:20 is preferred for long runs because it removes the need for handrails. Anything steeper than 1:10 is considered a short transition only — fine for a portable threshold ramp, but not for a primary access route."],
  ["What is the difference between rise and depth?",
   "Rise is the vertical height the ramp has to overcome (the step or doorway). Depth is the horizontal run length of the ramp on the ground. Depth = Rise × Gradient ratio — so a 150 mm rise at 1:14 needs 2,100 mm of depth on the floor."],
  ["Can I use a rubber ramp outdoors?",
   "Yes. Our recycled rubber ramps are made for both indoor and outdoor use. They stay non-slip in the wet, don't rust or rattle, and tolerate UV and temperature swings far better than aluminium. For exposed locations we recommend bolting larger ramps down for stability."],
  ["Do you make custom ramp sizes?",
   "Every rubber ramp we manufacture is cut to your specific rise, depth and width. There is no standard size you have to make your doorway fit — send us the measurements and we'll fabricate to suit. Same goes for timber ramps, infills and platforms."],
  ["Do you supply ramps Australia-wide?",
   "Yes. Rubber ramps, threshold strips and aluminium ramps ship from our Melbourne factory to every state and territory. Lead time is typically 5–10 business days for custom-cut units; in-stock sizes ship next business day."],
  ["Do you install ramps in Melbourne?",
   "Yes. Our installation team services greater Melbourne and Victoria for custom rubber ramps, timber ramps, platforms and handrails. Most installations are completed in 1–3 hours per ramp. Get a transparent supply-and-install quote through this calculator or by phone."],
  ["What information should I send for a quote?",
   "The fastest quote turnaround comes from sending: rise (mm), available depth (mm), preferred width (mm), one or two photos of the threshold, and the address for installation if applicable. A rough sketch or doorway drawing is a bonus but not required."],
];

export default function PublicSeoContent() {
  const [openIdx, setOpenIdx] = useState(-1);

  return (
    <div className="bg-white">
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-16" data-testid="seo-content">
        <section id="faq" data-testid="seo-faq">
          <div className="mb-6 md:mb-8 text-center">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#fda500] font-bold mb-2">FAQ</div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0A1128]">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-2.5">
            {FAQS.map(([q, a], i) => {
              const open = openIdx === i;
              return (
                <div key={q} className={`bg-white border rounded-xl transition-all ${open ? "border-[#254391] shadow-[0_4px_12px_rgba(10,17,40,0.06)]" : "border-slate-200"}`}>
                  <button
                    type="button"
                    onClick={() => setOpenIdx(open ? -1 : i)}
                    data-testid={`faq-q-${i}`}
                    className="w-full flex items-start justify-between gap-4 px-5 py-4 text-left"
                    aria-expanded={open}
                  >
                    <span className="text-sm md:text-base font-semibold text-[#0A1128] leading-relaxed">{q}</span>
                    <ChevronDown size={18} className={`flex-shrink-0 mt-0.5 transition-transform text-[#254391] ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <div className="px-5 pb-5 -mt-1 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3" data-testid={`faq-a-${i}`}>
                      {a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
